import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Grid,
  Divider,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as ShippingIcon,
  Assignment as AssignmentIcon,
  Waves as WavesIcon,
} from '@mui/icons-material';
import { ParteProduccionService } from '../../features/produccion/partes-produccion/services/ParteProduccionService';
import type { ParteProduccion } from '../../features/produccion/partes-produccion/types';
import { useAuth } from '../../auth/hooks/useAuth';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [partesAprobados, setPartesAprobados] = useState<ParteProduccion[]>([]);
  const [partesPendientes, setPartesPendientes] = useState<ParteProduccion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPartes();
  }, []);

  const loadPartes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ParteProduccionService.getAll(1, 100);
      const sortByNumeroDesc = (a: ParteProduccion, b: ParteProduccion) =>
        (b.numeroComprobante || 0) - (a.numeroComprobante || 0);

      setPartesAprobados(
        response.data.filter((p) => p.estado === 'APR').sort(sortByNumeroDesc)
      );
      setPartesPendientes(
        response.data.filter((p) => p.estado === 'PEN').sort(sortByNumeroDesc)
      );
    } catch (err) {
      console.error('Error cargando partes:', err);
      setError('Error al cargar los partes de produccion');
    } finally {
      setLoading(false);
    }
  };

  const handleParteClick = (parteId: number) => {
    navigate(`/produccion/partes-produccion/${parteId}?readonly=true`);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos dias';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const totalPartes = partesAprobados.length + partesPendientes.length;

  // --- Stat card component ---
  const StatCard = ({
    icon,
    label,
    value,
    color,
    bgColor,
  }: {
    icon: React.ReactNode;
    label: string;
    value: number;
    color: string;
    bgColor: string;
  }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: color,
          boxShadow: `0 4px 12px ${bgColor}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Avatar
        sx={{
          bgcolor: bgColor,
          color: color,
          width: 48,
          height: 48,
        }}
      >
        {icon}
      </Avatar>
      <Box>
        <Typography
          sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.8rem',
            color: '#64748b',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#0f2847',
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Paper>
  );

  // --- Partes list ---
  const renderPartesList = (
    partes: ParteProduccion[],
    tipo: 'aprobados' | 'pendientes'
  ) => {
    if (partes.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 5, color: '#94a3b8' }}>
          <AssignmentIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
          <Typography
            sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.9rem' }}
          >
            No hay partes {tipo}
          </Typography>
        </Box>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {partes.map((parte, index) => (
          <React.Fragment key={parte.id}>
            {index > 0 && <Divider sx={{ mx: 2 }} />}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleParteClick(parte.id)}
                sx={{
                  py: 2,
                  px: 2.5,
                  borderRadius: 2,
                  mx: 1,
                  my: 0.3,
                  '&:hover': {
                    backgroundColor: '#f0f7ff',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: tipo === 'aprobados' ? '#10b981' : '#f59e0b',
                    mr: 2,
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"DM Sans", sans-serif',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          color: '#0f2847',
                        }}
                      >
                        Parte N° {parte.numeroComprobante}
                      </Typography>
                      <Chip
                        label={formatDate(parte.fecha)}
                        size="small"
                        sx={{
                          fontFamily: '"DM Sans", sans-serif',
                          fontSize: '0.7rem',
                          height: 22,
                          bgcolor: '#f1f5f9',
                          color: '#64748b',
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: '"DM Sans", sans-serif',
                          fontSize: '0.82rem',
                          color: '#64748b',
                          mb: 0.3,
                        }}
                      >
                        {parte.cliente || 'Sin cliente'}
                      </Typography>
                      {parte.asociacion && (
                        <Typography
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontSize: '0.78rem',
                            color: '#94a3b8',
                          }}
                        >
                          {parte.asociacion}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {!parte.numeroGuiaRemision && (
                          <Chip
                            label="SIN GUIA REMITENTE"
                            size="small"
                            sx={{
                              fontSize: '0.6rem',
                              height: 18,
                              bgcolor: '#fef3c7',
                              color: '#92400e',
                              fontFamily: '"DM Sans", sans-serif',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {!parte.numeroGuiaRemisionTransportista && (
                          <Chip
                            label="SIN GUIA TRANSPORTISTA"
                            size="small"
                            sx={{
                              fontSize: '0.6rem',
                              height: 18,
                              bgcolor: '#fef3c7',
                              color: '#92400e',
                              fontFamily: '"DM Sans", sans-serif',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
                <VisibilityIcon
                  sx={{ color: '#cbd5e1', ml: 1, fontSize: 20 }}
                />
              </ListItemButton>
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#f8fafc',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <WavesIcon
            sx={{
              fontSize: 48,
              color: '#3b82f6',
              mb: 2,
              animation: 'wave-pulse 1.5s ease-in-out infinite',
              '@keyframes wave-pulse': {
                '0%, 100%': { opacity: 0.5, transform: 'scale(1)' },
                '50%': { opacity: 1, transform: 'scale(1.1)' },
              },
            }}
          />
          <CircularProgress size={32} sx={{ display: 'block', mx: 'auto' }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1, p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 2, md: 3 },
        bgcolor: '#f8fafc',
        minHeight: '100vh',
      }}
    >
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.85rem',
            color: '#94a3b8',
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {getGreeting()}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontSize: { xs: '1.6rem', md: '2rem' },
            fontWeight: 700,
            color: '#0f2847',
            lineHeight: 1.2,
          }}
        >
          {user?.name ? user.name : 'Dashboard'}
        </Typography>
        <Box
          sx={{
            width: 40,
            height: 3,
            background: 'linear-gradient(90deg, #3b82f6, #10b981)',
            borderRadius: 2,
            mt: 1.5,
          }}
        />
      </Box>

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard
            icon={<TrendingUpIcon />}
            label="Total Partes"
            value={totalPartes}
            color="#3b82f6"
            bgColor="rgba(59,130,246,0.1)"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <StatCard
            icon={<CheckCircleIcon />}
            label="Aprobados"
            value={partesAprobados.length}
            color="#10b981"
            bgColor="rgba(16,185,129,0.1)"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <StatCard
            icon={<ShippingIcon />}
            label="Pendientes"
            value={partesPendientes.length}
            color="#f59e0b"
            bgColor="rgba(245,158,11,0.1)"
          />
        </Grid>
      </Grid>

      {/* Partes Columns */}
      <Grid container spacing={3}>
        {/* Aprobados */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 3,
                py: 2,
                borderBottom: '1px solid #e5e7eb',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#10b981',
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: '#0f2847',
                  flex: 1,
                }}
              >
                Partes Aprobados
              </Typography>
              <Chip
                label={partesAprobados.length}
                size="small"
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  bgcolor: '#ecfdf5',
                  color: '#059669',
                  height: 26,
                }}
              />
            </Box>
            <Box sx={{ maxHeight: 550, overflow: 'auto', bgcolor: '#fafbfc' }}>
              {renderPartesList(partesAprobados, 'aprobados')}
            </Box>
          </Paper>
        </Grid>

        {/* Pendientes */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 3,
                py: 2,
                borderBottom: '1px solid #e5e7eb',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#f59e0b',
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: '#0f2847',
                  flex: 1,
                }}
              >
                Partes Pendientes
              </Typography>
              <Chip
                label={partesPendientes.length}
                size="small"
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  bgcolor: '#fffbeb',
                  color: '#b45309',
                  height: 26,
                }}
              />
            </Box>
            <Box sx={{ maxHeight: 550, overflow: 'auto', bgcolor: '#fafbfc' }}>
              {renderPartesList(partesPendientes, 'pendientes')}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
